import React from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { EmployeeSchedule, ScheduleDay } from '@/types/schedule.types';

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

interface ScheduleGridProps {
  employees: EmployeeSchedule[];
  dates: Date[];
  onCellClick?: (employee: EmployeeSchedule, day: ScheduleDay) => void;
}

const getDayColorClass = (
  isWorking: boolean,
  scheduleType: string,
  source: string,
): string => {
  // Exception has special styling
  if (source === 'schedule_exception') {
    if (isWorking) {
      return 'bg-amber-200 hover:bg-amber-300 border-amber-400';
    }
    return 'bg-amber-100 hover:bg-amber-200 border-amber-300';
  }

  // Normal schedule
  if (!isWorking || scheduleType === 'day_off') {
    return 'bg-gray-100 hover:bg-gray-200 border-gray-200';
  }

  switch (scheduleType) {
    case 'working':
      return 'bg-green-100 hover:bg-green-200 border-green-200';
    case 'night_shift':
      return 'bg-indigo-100 hover:bg-indigo-200 border-indigo-200';
    case 'holiday':
      return 'bg-red-100 hover:bg-red-200 border-red-200';
    case 'reduced':
      return 'bg-yellow-100 hover:bg-yellow-200 border-yellow-200';
    default:
      return 'bg-gray-50 hover:bg-gray-100 border-gray-100';
  }
};

const formatTime = (time: string | null | undefined): string => {
  if (!time) return '';
  const value = time.toString();
  const timePart = value.includes('T') ? value.split('T')[1] : value;
  return timePart.slice(0, 5); // HH:mm
};

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

export const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  employees,
  dates,
  onCellClick,
}) => {
  // Find schedule day for employee and date
  const findScheduleDay = (
    employee: EmployeeSchedule,
    date: Date,
  ): ScheduleDay | undefined => {
    const dateStr = date.toISOString().split('T')[0];
    return employee.schedule.find((s) => s.date === dateStr);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          {/* Header with dates */}
          <thead>
            <tr className="bg-muted">
              {/* Employee column header */}
              <th className="sticky left-0 z-20 bg-muted border-r min-w-[200px] p-2 text-left font-medium">
                Сотрудник
              </th>
              {/* Date headers */}
              {dates.map((date) => {
                const dayOfWeek = date.getDay();
                const dayNum = date.getDate();
                const isTodayDate = isToday(date);
                const isWeekendDay = isWeekend(date);

                return (
                  <th
                    key={date.toISOString()}
                    className={cn(
                      'min-w-[50px] p-1 text-center text-xs font-medium border-l',
                      isTodayDate && 'bg-blue-100 border-blue-300',
                      isWeekendDay && !isTodayDate && 'bg-gray-100',
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'text-[10px]',
                          isWeekendDay ? 'text-red-500' : 'text-muted-foreground',
                        )}
                      >
                        {WEEKDAY_NAMES[dayOfWeek]}
                      </span>
                      <span
                        className={cn(
                          'font-bold',
                          isTodayDate && 'text-blue-600',
                          isWeekendDay && !isTodayDate && 'text-red-500',
                        )}
                      >
                        {dayNum}
                      </span>
                    </div>
                  </th>
                );
              })}
              {/* Statistics columns */}
              <th className="min-w-[50px] p-1 text-center text-xs font-medium border-l bg-green-50">
                Раб.
              </th>
              <th className="min-w-[50px] p-1 text-center text-xs font-medium border-l bg-gray-100">
                Вых.
              </th>
            </tr>
          </thead>
          {/* Body with employees */}
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td
                  colSpan={dates.length + 3}
                  className="p-8 text-center text-muted-foreground"
                >
                  Нет сотрудников в отделе
                </td>
              </tr>
            ) : (
              employees.map((employee) => (
                <tr key={employee.employee_id} className="hover:bg-muted/50">
                  {/* Employee name */}
                  <td className="sticky left-0 z-10 bg-background border-r p-2 min-w-[200px]">
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {employee.last_name} {employee.first_name}
                      </span>
                      {employee.position && (
                        <span className="text-xs text-muted-foreground">
                          {employee.position}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* Schedule cells */}
                  {dates.map((date) => {
                    const scheduleDay = findScheduleDay(employee, date);
                    const isTodayDate = isToday(date);

                    if (!scheduleDay) {
                      return (
                        <td
                          key={date.toISOString()}
                          className={cn(
                            'p-0 border-l',
                            isTodayDate && 'ring-2 ring-blue-400 ring-inset',
                          )}
                        >
                          <div className="h-10 bg-gray-50 flex items-center justify-center">
                            <span className="text-[10px] text-gray-400">-</span>
                          </div>
                        </td>
                      );
                    }

                    const colorClass = getDayColorClass(
                      scheduleDay.is_working_day,
                      scheduleDay.schedule_type,
                      scheduleDay.source,
                    );

                    const timeDisplay = scheduleDay.is_working_day
                      ? `${formatTime(scheduleDay.shift_start)} - ${formatTime(scheduleDay.shift_end)}`
                      : 'Вых';

                    const tooltipContent = (
                      <div className="text-xs space-y-1">
                        <div className="font-medium">
                          {scheduleDay.schedule_type === 'night_shift'
                            ? 'Ночная смена'
                            : scheduleDay.is_working_day
                              ? 'Рабочий день'
                              : 'Выходной'}
                        </div>
                        {scheduleDay.is_working_day && (
                          <div>
                            {formatTime(scheduleDay.shift_start)} -{' '}
                            {formatTime(scheduleDay.shift_end)}
                          </div>
                        )}
                        <div className="text-muted-foreground">
                          Источник:{' '}
                          {scheduleDay.source === 'schedule_exception'
                            ? 'Исключение'
                            : scheduleDay.source === 'template_calendar'
                              ? 'Календарь шаблона'
                              : scheduleDay.source === 'work_schedule'
                                ? 'Персональный график'
                                : 'Паттерн шаблона'}
                        </div>
                        {scheduleDay.exception && (
                          <>
                            {scheduleDay.exception.reason && (
                              <div>Причина: {scheduleDay.exception.reason}</div>
                            )}
                            {scheduleDay.exception.is_replacement && (
                              <div className="text-amber-600">
                                Замена: {scheduleDay.exception.replacement_employee_name}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );

                    return (
                      <td
                        key={date.toISOString()}
                        className={cn(
                          'p-0 border-l',
                          isTodayDate && 'ring-2 ring-blue-400 ring-inset',
                        )}
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => onCellClick?.(employee, scheduleDay)}
                                className={cn(
                                  'h-10 w-full flex flex-col items-center justify-center border cursor-pointer transition-colors',
                                  colorClass,
                                  scheduleDay.source === 'schedule_exception' &&
                                    'border-2 border-dashed',
                                )}
                              >
                                {scheduleDay.is_working_day ? (
                                  <>
                                    <span className="text-[10px] font-medium leading-tight">
                                      {formatTime(scheduleDay.shift_start)}
                                    </span>
                                    {scheduleDay.schedule_type === 'night_shift' && (
                                      <span className="text-[8px] text-indigo-600 font-medium">ноч</span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-[10px] text-gray-500">вых</span>
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              {tooltipContent}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    );
                  })}
                  {/* Statistics cells */}
                  <td className="p-1 text-center text-sm font-medium border-l bg-green-50">
                    {employee.total_working_days}
                  </td>
                  <td className="p-1 text-center text-sm font-medium border-l bg-gray-100">
                    {employee.total_days_off}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Legend */}
      <div className="border-t p-3 flex flex-wrap gap-4 text-xs bg-muted/30">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
          <span className="text-muted-foreground">Рабочий день</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-indigo-100 border border-indigo-200" />
          <span className="text-muted-foreground">Ночная смена</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
          <span className="text-muted-foreground">Выходной</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 border border-red-200" />
          <span className="text-muted-foreground">Праздник</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200" />
          <span className="text-muted-foreground">Сокращённый</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-amber-200 border-2 border-dashed border-amber-400" />
          <span className="text-muted-foreground">Исключение</span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleGrid;
